import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { projectCostService } from '../../../services/projectCostService';
import Icon from '../../../components/AppIcon';
import SearchableSelect from '../../../components/ui/SearchableSelect';
import useTranslation from '../../../hooks/useTranslation';
import useEscapeKey from '../../../hooks/useEscapeKey';

// ── helpers ──────────────────────────────────────────────────────────────────

/** Format a numeric string / number as "1,234.56" */
const formatAmount = (raw) => {
  const num = parseFloat(String(raw)?.replace(/,/g, ''));
  if (isNaN(num)) return '';
  return num?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** Strip formatting and return a plain numeric string */
const stripFormat = (str) => String(str)?.replace(/,/g, '');

// Shared input class — single source of truth for all fields
const inputCls =
  'w-full px-3 py-2 text-xs border border-gray-200 rounded-lg outline-none ' + 'focus:border-primary focus:ring-2 focus:ring-primary/20 bg-white transition-colors '+ 'disabled:opacity-50 disabled:cursor-not-allowed';

const ALLOWED_TYPES = [
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
];

const ALLOWED_EXTENSIONS = ['pdf', 'xls', 'xlsx', 'doc', 'docx', 'csv'];

const sanitizeFilename = (name) =>
  name?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '')?.toLowerCase()
    ?.replace(/\s+/g, '_')?.replace(/[^a-z0-9_.]/g, '')?.replace(/_+/g, '_');

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024)?.toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024))?.toFixed(1)} MB`;
};

const getFileTypeIcon = (fileName) => {
  const ext = fileName?.split('.')?.pop()?.toLowerCase();
  if (ext === 'pdf') return <Icon name="FileText" size={14} className="text-red-500 flex-shrink-0" />;
  if (['xls', 'xlsx', 'csv']?.includes(ext)) return <Icon name="FileSpreadsheet" size={14} className="text-green-600 flex-shrink-0" />;
  if (['doc', 'docx']?.includes(ext)) return <Icon name="FileText" size={14} className="text-blue-500 flex-shrink-0" />;
  return <Icon name="File" size={14} className="text-gray-400 flex-shrink-0" />;
};

// ─────────────────────────────────────────────────────────────────────────────

const AddContractModal = ({ isOpen, onClose, onSubmit, projectId, initialData }) => {
  const { t } = useTranslation();
  const isEditMode = !!initialData;

  const [subcontractors, setSubcontractors] = useState([]);
  const [projectCosts, setProjectCosts] = useState([]);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState(null);
  const [selectedProjectCost, setSelectedProjectCost] = useState(null);

  // Raw numeric value (no formatting) kept for calculations / submit
  const [contractValueRaw, setContractValueRaw] = useState('');
  // Display value shown in the input (formatted on blur)
  const [contractValueDisplay, setContractValueDisplay] = useState('');

  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [documentType, setDocumentType] = useState('contract');
  const [pricingModel, setPricingModel] = useState('fixed');

  // ── Document upload state (create mode only) ──────────────────────────────
  const [pendingFiles, setPendingFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, projectId]);

  // Prefill form when editing
  useEffect(() => {
    if (isOpen && initialData && subcontractors?.length > 0 && projectCosts?.length > 0) {
      prefillForm();
    }
  }, [isOpen, initialData, subcontractors, projectCosts]);

  const prefillForm = () => {
    if (!initialData) return;

    const sub = subcontractors?.find(s => String(s?.id) === String(initialData?.subcontractorId || initialData?.subcontractor?.id));
    setSelectedSubcontractor(sub || null);

    const pc = projectCosts?.find(p => String(p?.id) === String(initialData?.projectCostId || initialData?.projectCost?.id));
    setSelectedProjectCost(pc || null);

    const rawVal = initialData?.contractValue ? String(initialData?.contractValue) : '';
    setContractValueRaw(rawVal);
    setContractValueDisplay(rawVal ? formatAmount(rawVal) : '');

    setNotes(initialData?.notes || '');
    setDocumentType(initialData?.documentType || 'contract');
    setPricingModel(initialData?.pricingModel || 'fixed');
  };

  const fetchData = async () => {
    setFetchingData(true);
    try {
      const [allCostCodes, subsResult] = await Promise.all([
        projectCostService?.getByProjectId(projectId),
        supabase?.from('subcontractors')?.select('id, company_name, trade')?.order('company_name', { ascending: true })
      ]);
      const { data: subs } = subsResult;

      setProjectCosts(allCostCodes || []);
      setSubcontractors(subs || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setFetchingData(false);
    }
  };

  const handleSubcontractorChange = (value) => {
    const subcontractor = subcontractors?.find(s => String(s?.id) === String(value));
    setSelectedSubcontractor(subcontractor || null);
  };

  const handleProjectCostChange = (value) => {
    const projectCost = projectCosts?.find(pc => String(pc?.id) === String(value));
    setSelectedProjectCost(projectCost || null);
  };

  // ── Amount field handlers ──────────────────────────────────────────────────

  const handleAmountChange = (e) => {
    const raw = e?.target?.value?.replace(/[^0-9.]/g, '');
    setContractValueRaw(raw);
    setContractValueDisplay(raw);
  };

  const handleAmountBlur = () => {
    if (contractValueRaw) {
      setContractValueDisplay(formatAmount(contractValueRaw));
    }
  };

  const handleAmountFocus = () => {
    setContractValueDisplay(contractValueRaw);
  };

  // ── File upload handlers ───────────────────────────────────────────────────

  const validateAndAddFiles = (fileList) => {
    setFileError(null);
    const MAX_SIZE = 20 * 1024 * 1024;
    const errors = [];
    const valid = [];

    Array.from(fileList || [])?.forEach(file => {
      const ext = file?.name?.split('.')?.pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS?.includes(ext) && !ALLOWED_TYPES?.includes(file?.type)) {
        errors?.push(`"${file?.name}": Unsupported file type. Use PDF, Excel, or Word.`);
        return;
      }
      if (file?.size > MAX_SIZE) {
        errors?.push(`"${file?.name}": File too large (max 20MB).`);
        return;
      }
      valid?.push(file);
    });

    if (errors?.length) setFileError(errors?.join('\n'));
    if (valid?.length) {
      setPendingFiles(prev => {
        const existingNames = new Set(prev?.map(f => f?.name));
        const newFiles = valid?.filter(f => !existingNames?.has(f?.name));
        return [...prev, ...newFiles];
      });
    }
  };

  const handleFileInputChange = (e) => {
    validateAndAddFiles(e?.target?.files);
    if (fileInputRef?.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    validateAndAddFiles(e?.dataTransfer?.files);
  };

  const handleDragOver = (e) => { e?.preventDefault(); setIsDragOver(true); };
  const handleDragEnter = (e) => { e?.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = () => setIsDragOver(false);

  const removePendingFile = (fileName) => {
    setPendingFiles(prev => prev?.filter(f => f?.name !== fileName));
  };

  // Upload pending files to storage after contract is created
  const uploadPendingFiles = async (contractId) => {
    if (!pendingFiles?.length) return;
    setUploadingFiles(true);
    const storagePath = `${projectId}/${contractId}`;
    for (const file of pendingFiles) {
      const sanitizedName = sanitizeFilename(file?.name);
      const filePath = `${storagePath}/${sanitizedName}`;
      try {
        await supabase?.storage
          ?.from('contracts-documents')
          ?.upload(filePath, file, { upsert: true, cacheControl: '3600' });
      } catch (err) {
        console.error('Error uploading file:', file?.name, err);
      }
    }
    setUploadingFiles(false);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!selectedProjectCost) {
      alert(t('contracts.addModal.validationCostCode'));
      return;
    }

    if (!selectedSubcontractor) {
      alert(t('contracts.addModal.validationSubcontractor'));
      return;
    }

    let finalContractValue = null;
    if (pricingModel === 'fixed') {
      const value = parseFloat(stripFormat(contractValueRaw));
      if (isNaN(value) || value <= 0) {
        alert(t('contracts.addModal.validationContractValue'));
        return;
      }
      finalContractValue = value;
    }

    setLoading(true);
    try {
      const name = `${selectedProjectCost?.description} - ${selectedSubcontractor?.company_name}`;
      const company = selectedSubcontractor?.company_name;
      const trade = selectedSubcontractor?.trade;

      const newContract = await onSubmit({
        projectId,
        projectCostId: selectedProjectCost?.id,
        subcontractorId: selectedSubcontractor?.id,
        name,
        company,
        trade,
        contractValue: finalContractValue,
        originalContract: finalContractValue,
        notes: notes?.trim() || null,
        status: initialData?.status || 'draft',
        documentType,
        pricingModel
      });

      // Upload pending files after contract is created (create mode only)
      if (!isEditMode && pendingFiles?.length > 0 && newContract?.id) {
        await uploadPendingFiles(newContract?.id);
      }

      resetForm();
      onClose();
    } catch (error) {
      console.error('Error saving contract:', error);
      alert(isEditMode ? t('contracts.addModal.failedUpdate') : t('contracts.addModal.failedCreate'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedSubcontractor(null);
    setSelectedProjectCost(null);
    setContractValueRaw('');
    setContractValueDisplay('');
    setNotes('');
    setDocumentType('contract');
    setPricingModel('fixed');
    setPendingFiles([]);
    setFileError(null);
    setIsDragOver(false);
  };

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  // ── Sorted option lists ────────────────────────────────────────────────────

  const sortedCostCodeOptions = [...(projectCosts || [])]
    ?.sort((a, b) => (a?.costCode || '')?.localeCompare(b?.costCode || ''))
    ?.map(pc => ({
      value: String(pc?.id),
      label: `${pc?.costCode} – ${pc?.description}`,
      searchText: `${pc?.costCode} ${pc?.description}`?.toLowerCase()
    }));

  const sortedSubcontractorOptions = [...(subcontractors || [])]
    ?.sort((a, b) => (a?.company_name || '')?.localeCompare(b?.company_name || ''))
    ?.map(sub => ({
      value: String(sub?.id),
      label: sub?.company_name,
      searchText: sub?.company_name?.toLowerCase()
    }));

  const pricingModelOptions = [
    {
      value: 'fixed',
      label: t('contracts.pricingModelOptions.fixed'),
      searchText: t('contracts.pricingModelOptions.fixed')?.toLowerCase()
    },
    {
      value: 'labor_material',
      label: t('contracts.pricingModelOptions.laborMaterial'),
      searchText: t('contracts.pricingModelOptions.laborMaterial')?.toLowerCase()
    },
    {
      value: 'material_supply',
      label: t('contracts.pricingModelOptions.materialSupply'),
      searchText: t('contracts.pricingModelOptions.materialSupply')?.toLowerCase()
    }
  ];

  const documentTypeOptions = [
    {
      value: 'contract',
      label: t('contracts.documentTypeOptions.contract'),
      searchText: t('contracts.documentTypeOptions.contract')?.toLowerCase()
    },
    {
      value: 'purchase_order',
      label: t('contracts.documentTypeOptions.purchaseOrder'),
      searchText: t('contracts.documentTypeOptions.purchaseOrder')?.toLowerCase()
    }
  ];

  // Echap = meme action que le bouton Annuler ; inactif pendant l'enregistrement.
  useEscapeKey(handleCancel, !!isOpen && !loading);

  if (!isOpen) return null;

  return (
    // Modale de formulaire : pas de fermeture au clic exterieur.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh] overflow-hidden" role="dialog" aria-modal="true">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
              <Icon name="FileText" size={18} className="text-slate-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-gray-900 leading-snug truncate">
                {isEditMode ? t('contracts.addModal.editTitle') : t('contracts.addContract')}
              </h2>
              <p className="text-[11px] text-gray-500 truncate">
                {isEditMode ? t('contracts.addModal.editSubtitle') : t('contracts.createSubcontractAgreement')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {fetchingData ? (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" size={32} className="animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Row 1: Document Type | Pricing Model */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    {t('contracts.documentType')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <SearchableSelect
                      options={documentTypeOptions}
                      value={documentType}
                      onChange={(value) => setDocumentType(value || 'contract')}
                      placeholder={t('contracts.documentType')}
                      className="w-full"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    {t('contracts.pricingModel')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <SearchableSelect
                      options={pricingModelOptions}
                      value={pricingModel}
                      onChange={(value) => setPricingModel(value || 'fixed')}
                      placeholder={t('contracts.pricingModel')}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Row 2: Cost Code | Subcontractor */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    {t('contracts.addModal.costCodeLabel')}
                  </label>
                  <SearchableSelect
                    options={sortedCostCodeOptions}
                    value={String(selectedProjectCost?.id || '')}
                    onChange={handleProjectCostChange}
                    placeholder={t('contracts.addModal.costCodePlaceholder')}
                    className="w-full"
                  />
                  {projectCosts?.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t('contracts.addModal.noCostCodes')}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    {t('contracts.addModal.subcontractorLabel')}
                  </label>
                  <SearchableSelect
                    options={sortedSubcontractorOptions}
                    value={String(selectedSubcontractor?.id || '')}
                    onChange={handleSubcontractorChange}
                    placeholder={t('contracts.addModal.subcontractorPlaceholder')}
                    className="w-full"
                  />
                  {subcontractors?.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t('contracts.addModal.noSubcontractors')}
                    </p>
                  )}
                </div>
              </div>

              {/* Row 3: Contract Value — $ prefix inside input */}
              {pricingModel === 'fixed' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    {t('contracts.addModal.contractValueLabel')}
                  </label>
                  <div className="relative flex items-center">
                    <span className="absolute left-3 text-xs text-gray-500 select-none pointer-events-none">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={contractValueDisplay}
                      onChange={handleAmountChange}
                      onBlur={handleAmountBlur}
                      onFocus={handleAmountFocus}
                      placeholder="0.00"
                      required
                      className={`${inputCls} pl-6`}
                    />
                  </div>
                </div>
              )}

              {/* Row 4: Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  {t('contracts.addModal.notesLabel')}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e?.target?.value)}
                  placeholder={t('contracts.addModal.notesPlaceholder')}
                  rows={3}
                  className={`${inputCls} resize-none`}
                />
              </div>

              {/* Row 5: Documents (create mode only) */}
              {!isEditMode && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Documents <span className="text-gray-400 font-normal">(optional)</span>
                  </label>

                  {/* File error */}
                  {fileError && (
                    <div className="mb-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 whitespace-pre-line">
                      {fileError}
                    </div>
                  )}

                  {/* Pending files list */}
                  {pendingFiles?.length > 0 && (
                    <ul className="mb-2 space-y-1">
                      {pendingFiles?.map((file) => (
                        <li key={file?.name} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-100">
                          {getFileTypeIcon(file?.name)}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate">{file?.name}</p>
                            <p className="text-[10px] text-gray-400">{formatFileSize(file?.size)}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => removePendingFile(file?.name)}
                            className="p-1 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                            title="Remove file"
                          >
                            <Icon name="X" size={12} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Upload drop zone */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.xls,.xlsx,.doc,.docx,.csv"
                    className="hidden"
                    onChange={handleFileInputChange}
                  />
                  <div
                    onClick={() => fileInputRef?.current?.click()}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    className={`border-2 border-dashed rounded-xl px-4 py-5 text-center cursor-pointer transition-all ${
                      isDragOver
                        ? 'border-primary bg-orange-50 scale-[1.01]'
                        : 'border-gray-200 hover:border-primary hover:bg-orange-50/30'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1.5">
                      <Icon name="Upload" size={18} className={isDragOver ? 'text-primary' : 'text-gray-400'} />
                      <p className="text-xs font-medium text-gray-600">
                        Drop files here or <span className="text-primary">click to upload</span>
                      </p>
                      <p className="text-[10px] text-gray-400">PDF, Excel, Word supported · Max 20MB each</p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

        </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className="px-3.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('contracts.addModal.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || fetchingData}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={13} className="animate-spin" />
                  <span>
                    {uploadingFiles
                      ? t('documents.uploading') : isEditMode ? t('contracts.addModal.saving') : t('contracts.addModal.adding')
                    }
                  </span>
                </>
              ) : (
                <>
                  <Icon name="Check" size={13} />
                  {isEditMode ? t('contracts.addModal.saveChanges') : t('contracts.addModal.addContract')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContractModal;